/**
 * ModelViewer - 通用3D模型查看器组件
 * 从POCPage.jsx提取的核心3D渲染逻辑
 */

import { useRef, useEffect, Suspense, useState, Component, forwardRef, useImperativeHandle } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, Environment, useGLTF, Html, Center, ContactShadows } from '@react-three/drei';
import * as THREE from 'three';
import { useLanguage } from '../../context/LanguageContext';
import styles from './ModelViewer.module.css';

// ============================================
// 错误边界组件
// ============================================
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('3D Viewer Error:', error, errorInfo);
        if (this.props.onError) {
            this.props.onError(error);
        }
    }

    render() {
        if (this.state.hasError) {
            const { errorTitle, errorMessage } = this.props;
            return (
                <div className={styles.errorContainer}>
                    <h3>⚠️ {errorTitle}</h3>
                    <p>{this.state.error?.message || errorMessage}</p>
                </div>
            );
        }
        return this.props.children;
    }
}

// ============================================
// 相机自动适配组件 - 在 OrbitControls 之后运行
// ============================================
function CameraAutoFit({ modelBounds, onComplete }) {
    const { camera, controls } = useThree();
    const hasRun = useRef(false);

    useEffect(() => {
        if (!modelBounds || hasRun.current) return;
        if (!controls) return; // 等待 controls 可用

        const { center, maxDim } = modelBounds;

        // 计算最佳相机距离
        const fov = camera.fov * (Math.PI / 180);
        let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2)) * 1.5;
        cameraZ = Math.max(cameraZ, 2);

        // 设置相机位置
        const cameraY = center.y + maxDim * 0.3;
        camera.position.set(cameraZ * 0.5, cameraY, cameraZ);

        // 更新 OrbitControls 的目标点（这是关键）
        controls.target.set(center.x, center.y, center.z);
        controls.update();

        camera.updateProjectionMatrix();
        hasRun.current = true;

        if (onComplete) {
            onComplete();
        }

        console.log('Camera auto-fit completed, distance:', cameraZ.toFixed(2));
    }, [modelBounds, camera, controls, onComplete]);

    return null;
}

// ============================================
// 3D模型渲染组件 - 带自动相机适配
// ============================================
function Model3D({
    modelUrl,
    hiddenNodes = [],
    scale = 1,
    position = [0, 0, 0],
    rotation = [0, 0, 0],
    onNodeListReady,
    onModelBounds,
}) {
    const groupRef = useRef();
    const { scene } = useGLTF(modelUrl);
    const meshMapRef = useRef({});

    // 首次加载时分析模型结构
    useEffect(() => {
        if (!scene) return;

        const nodes = [];
        const meshMap = {};

        // 计算整个场景的边界框
        const sceneBox = new THREE.Box3().setFromObject(scene);
        const sceneSize = new THREE.Vector3();
        const sceneCenter = new THREE.Vector3();
        sceneBox.getSize(sceneSize);
        sceneBox.getCenter(sceneCenter);

        scene.traverse((child) => {
            if (child.name && child.isMesh) {
                meshMap[child.name] = child;

                // 计算每个mesh的边界框
                const box = new THREE.Box3().setFromObject(child);
                const center = new THREE.Vector3();
                const size = new THREE.Vector3();
                box.getCenter(center);
                box.getSize(size);

                const relativeSize = size.length() / sceneSize.length();

                // 启用阴影
                child.castShadow = true;
                child.receiveShadow = true;

                nodes.push({
                    name: child.name,
                    type: child.type,
                    size: relativeSize,
                    sizeLabel: relativeSize > 0.1 ? 'large' : relativeSize > 0.03 ? 'medium' : 'small',
                });
            }
        });

        // 按大小排序
        nodes.sort((a, b) => b.size - a.size);
        meshMapRef.current = meshMap;

        const maxDim = Math.max(sceneSize.x, sceneSize.y, sceneSize.z);

        // 通知父组件边界信息（用于相机适配）
        if (onModelBounds) {
            onModelBounds({
                size: sceneSize,
                center: sceneCenter,
                maxDim,
            });
        }

        if (onNodeListReady) {
            onNodeListReady(nodes);
        }

        console.log('Model loaded:', nodes.length, 'mesh nodes, size:', maxDim.toFixed(2));
    }, [scene, onNodeListReady, onModelBounds]);

    // 根据hiddenNodes控制显示/隐藏
    useEffect(() => {
        Object.entries(meshMapRef.current).forEach(([name, mesh]) => {
            if (mesh) {
                mesh.visible = !hiddenNodes.includes(name);
            }
        });
    }, [hiddenNodes]);

    return (
        <group ref={groupRef} dispose={null}>
            <primitive
                object={scene}
                scale={scale}
                position={position}
                rotation={rotation}
            />
        </group>
    );
}

// ============================================
// 加载指示器
// ============================================
function Loader({ message }) {
    const { t } = useLanguage();
    const displayMessage = message || t('viewer3d.loading');
    return (
        <Html center>
            <div className={styles.loader}>
                <div className={styles.spinner}></div>
                <p>{displayMessage}</p>
            </div>
        </Html>
    );
}

// ============================================
// 相机控制组件
// ============================================
function CameraController({ height = 1.5, distance = 5, lookAtY = 1 }) {
    const { camera } = useThree();

    useEffect(() => {
        camera.position.set(0, height, distance);
        camera.lookAt(0, lookAtY, 0);
        camera.updateProjectionMatrix();
    }, [camera, height, distance, lookAtY]);

    return null;
}

// ============================================
// 主 ModelViewer 组件
// ============================================
export default function ModelViewer({
    modelUrl,
    hiddenNodes = [],
    onNodeListReady,
    // 模型配置
    modelScale = 1,
    modelPosition = [0, 0, 0],
    modelRotation = [0, 0, 0],
    // 相机配置
    cameraHeight = 1.5,
    cameraDistance = 5,
    cameraLookAtY = 1,
    // 环境配置
    environmentPreset = 'studio',
    backgroundColor = '#ffffff',
    showShadows = true,
    // 控制配置
    enableOrbit = true,
    enablePan = true,
    minDistance = 2,
    maxDistance = 15,
    // 尺寸配置
    width = '100%',
    height = '100%',
    // 回调
    onLoad,
    onError,
}) {
    const { t } = useLanguage();
    const [error, setError] = useState(null);
    const [modelBounds, setModelBounds] = useState(null);

    const handleNodeListReady = (nodes) => {
        if (onNodeListReady) {
            onNodeListReady(nodes);
        }
        if (onLoad) {
            onLoad();
        }
    };

    const handleModelBounds = (bounds) => {
        setModelBounds(bounds);
    };

    const handleError = (err) => {
        console.error('ModelViewer error:', err);
        setError(err);
        if (onError) {
            onError(err);
        }
    };

    // 验证 modelUrl
    if (!modelUrl) {
        return (
            <div className={styles.errorContainer}>
                <h3>⚠️ {t('viewer3d.error.noModelUrl')}</h3>
                <p>{t('viewer3d.error.configureUrl')}</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorContainer}>
                <h3>⚠️ {t('viewer3d.error.loadFailed')}</h3>
                <p>{error.message || t('viewer3d.error.checkConnection')}</p>
            </div>
        );
    }

    return (
        <div className={styles.container} style={{ width, height }}>
            <ErrorBoundary
                onError={handleError}
                errorTitle={t('viewer3d.error.loadFailed')}
                errorMessage={t('viewer3d.error.checkConnection')}
            >
                <Canvas
                    gl={{ antialias: true, alpha: false }}
                    shadows={showShadows}
                    camera={{ fov: 45, near: 0.1, far: 1000, position: [0, 2, 10] }}
                    onCreated={({ gl }) => {
                        gl.setClearColor(backgroundColor);
                    }}
                >
                    {/* 背景色 */}
                    <color attach="background" args={[backgroundColor]} />

                    <Suspense fallback={<Loader />}>
                        {/* 光照 */}
                        <ambientLight intensity={0.6} />
                        <directionalLight
                            position={[5, 10, 5]}
                            intensity={1.5}
                            color="#ffffff"
                            castShadow={showShadows}
                            shadow-mapSize-width={2048}
                            shadow-mapSize-height={2048}
                            shadow-camera-far={50}
                            shadow-camera-left={-10}
                            shadow-camera-right={10}
                            shadow-camera-top={10}
                            shadow-camera-bottom={-10}
                            shadow-bias={-0.0001}
                        />
                        <directionalLight position={[-5, 8, -5]} intensity={0.5} color="#ffffff" />
                        <directionalLight position={[0, 5, 10]} intensity={0.6} color="#ffffff" />

                        {/* 环境反射 */}
                        <Environment preset={environmentPreset} background={false} />

                        {/* 轨道控制 - 必须先渲染，使用 makeDefault 让其他组件可以访问 */}
                        {enableOrbit && (
                            <OrbitControls
                                makeDefault
                                enablePan={enablePan}
                                enableRotate={true}
                                enableZoom={true}
                                minDistance={minDistance}
                                maxDistance={maxDistance * 5}
                                maxPolarAngle={Math.PI * 0.9}
                            />
                        )}

                        {/* 3D模型 */}
                        <Model3D
                            modelUrl={modelUrl}
                            hiddenNodes={hiddenNodes}
                            scale={modelScale}
                            position={modelPosition}
                            rotation={modelRotation}
                            onNodeListReady={handleNodeListReady}
                            onModelBounds={handleModelBounds}
                        />

                        {/* 相机自动适配 - 在 OrbitControls 之后 */}
                        <CameraAutoFit modelBounds={modelBounds} />

                        {/* 接触阴影 - 自动适应模型大小 */}
                        {showShadows && (
                            <>
                                <ContactShadows
                                    position={[0, 0, 0]}
                                    opacity={0.4}
                                    scale={50}
                                    blur={2.5}
                                    far={20}
                                    resolution={1024}
                                    color="#000000"
                                />
                                <mesh
                                    rotation={[-Math.PI / 2, 0, 0]}
                                    position={[0, -0.01, 0]}
                                    receiveShadow
                                >
                                    <planeGeometry args={[100, 100]} />
                                    <shadowMaterial opacity={0.2} />
                                </mesh>
                            </>
                        )}
                    </Suspense>
                </Canvas>
            </ErrorBoundary>
        </div>
    );
}

// 导出子组件供高级用法
export { Model3D, Loader, CameraController };
